import { Trophy, Star, TrendingUp, Zap } from 'lucide-react';
import type { RankedEntity } from '../types/statistics';
import { formatEuro } from '../utils/currency';

interface InfluencerRankingsProps {
  influencers: RankedEntity[];
  loading?: boolean;
}

export function InfluencerRankings({ influencers, loading = false }: InfluencerRankingsProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-pulse rounded-2xl bg-gray-100 p-6 h-32"></div>
        ))}
      </div>
    );
  }

  if (!influencers || influencers.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">Nenhum influenciador encontrado</p>
      </div>
    );
  }

  const getBadgeConfig = (badge?: string) => {
    switch (badge) {
      case 'Top Performer':
        return { icon: Star, text: 'Top Performer', color: 'bg-gradient-to-r from-yellow-400 to-orange-500' };
      case 'Rising Star':
        return { icon: TrendingUp, text: 'Rising Star', color: 'bg-gradient-to-r from-pink-400 to-rose-500' };
      case 'Active':
        return { icon: Zap, text: 'Ativo', color: 'bg-gradient-to-r from-blue-400 to-cyan-500' };
      default:
        return null;
    }
  };

  const topThree = influencers.slice(0, 3);
  const rest = influencers.slice(3);

  return (
    <div className="space-y-4">
      {topThree.map((influencer) => {
        const badgeConfig = getBadgeConfig(influencer.badge);

        return (
          <div
            key={influencer.id}
            className="relative bg-gradient-to-br from-purple-50 to-purple-100/50 border-2 border-purple-400 rounded-2xl p-6 hover:shadow-xl transition-all duration-300"
          >
            <div className="absolute -left-4 top-1/2 -translate-y-1/2 bg-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl shadow-lg">
              {influencer.rank}
            </div>

            <div className="flex items-start justify-between ml-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Trophy className="text-yellow-500" size={24} />
                  <h3 className="text-xl font-bold text-gray-900">{influencer.name}</h3>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  {badgeConfig && (
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-bold text-white ${badgeConfig.color}`}>
                      <badgeConfig.icon size={14} />
                      {badgeConfig.text}
                    </span>
                  )}
                  <span className="text-gray-600 text-sm">{influencer.campaign_count} campanhas</span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/60 rounded-xl p-3">
                    <p className="text-xs text-gray-600 mb-1">Taxa de Entrega</p>
                    <p className="text-lg font-bold text-gray-900">{influencer.delivery_rate || 95}%</p>
                  </div>
                  <div className="bg-white/60 rounded-xl p-3">
                    <p className="text-xs text-gray-600 mb-1">Média/Campanha</p>
                    <p className="text-lg font-bold text-gray-900">{formatEuro(influencer.average_value)}</p>
                  </div>
                </div>

                {influencer.commission_generated !== undefined && (
                  <div className="mt-3 bg-purple-100 rounded-xl p-3">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold text-purple-700">Comissão Gerada:</span>{' '}
                      <span className="font-bold text-purple-900">{formatEuro(influencer.commission_generated)}</span>
                    </p>
                  </div>
                )}
              </div>

              <div className="text-right">
                <p className="text-4xl font-bold text-purple-600">{formatEuro(influencer.value)}</p>
                <p className="text-sm text-gray-600 mt-1">Total Recebido</p>
              </div>
            </div>

            {influencer.delivery_rate !== undefined && (
              <div className="mt-4 ml-6">
                <div className="w-full bg-purple-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${influencer.delivery_rate}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {rest.map((influencer) => {
        const badgeConfig = getBadgeConfig(influencer.badge);

        return (
          <div
            key={influencer.id}
            className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                <div className="bg-gray-200 text-gray-700 rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                  {influencer.rank}
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{influencer.name}</h3>
                    {badgeConfig && (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-bold text-white ${badgeConfig.color}`}>
                        <badgeConfig.icon size={12} />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {influencer.campaign_count} campanhas • Média: {formatEuro(influencer.average_value)}
                    {influencer.commission_generated !== undefined && (
                      <> • <span className="text-purple-600 font-semibold">+{formatEuro(influencer.commission_generated)} comissão</span></>
                    )}
                  </p>
                </div>
              </div>

              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{formatEuro(influencer.value)}</p>
                {influencer.delivery_rate !== undefined && (
                  <p className="text-sm text-gray-600">{influencer.delivery_rate}% entrega</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
